import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Sidebar } from './Sidebar';
import { PatientHistorySidebar } from './PatientHistorySidebar';
import { AISidebar } from '@/components/ai-sidebar/AISidebar';
import { AIAssistantTrigger } from '@/components/ai-sidebar/AIAssistantTrigger';
import { ToastContainer } from '@/components/ui/Toast';
import { VideoCallProvider } from '@/v5/contexts/VideoCallContext';
import { FloatingVideoCall } from '@/v5/components/FloatingVideoCall';
import { VideoCallConfirmDialog } from '@/v5/components/VideoCallConfirmDialog';
import { VoiceCallProvider } from '@/v5/contexts/VoiceCallContext';
import { FloatingVoiceCall } from '@/v5/components/FloatingVoiceCall';
import { NoteFormProvider } from '@/v5/contexts/NoteFormContext';
import { FloatingNoteForm } from '@/v5/components/FloatingNoteForm';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useNotificationPopups } from '@/hooks/useNotificationPopups';
import { useFrontDeskArrivals } from '@/hooks/useFrontDeskArrivals';
import { useScreenTracking } from '@/hooks/useScreenTracking';
import { NotificationModalHost } from './NotificationModalHost';
import { authApi } from '@/services/api';
import { tzRulesSource } from '@/lib/tzRules';

export function Layout() {
  const token = useAuthStore((s) => s.token);
  const accountId = useAuthStore((s) => s.accountId);
  // Pages memoize times by zone name, which doesn't change when the server's
  // offset rules first arrive (SCRUM-2428). Keying the page on whether they
  // have renders it again with them, once: only a session signed in before
  // the rules existed starts without them. Later rule changes (a zone picked
  // in the user menu) keep the page, and with it any unsaved edits.
  const tzRulesKey = useAuthStore((s) => tzRulesSource(s.user?.tzRules));
  const updateUser = useAuthStore((s) => s.updateUser);
  const setModules = useAuthStore((s) => s.setModules);
  const setOwnerAgreementHold = useAuthStore((s) => s.setOwnerAgreementHold);
  // "AI Assistant" permission (id 26) gates the contextual sidebar in all envs.
  const { hasPermission } = useAuth();

  // Bottom-right pop-up notifications (legacy parity with
  // html/assets-ftp/js/nav.js `checkForNotifications`).
  useNotificationPopups();

  // Front desk tap pads: opens the tapped patient's chart on this computer when
  // this computer is set up as a front desk reader.
  useFrontDeskArrivals();

  // User Audit: report each screen this staff member opens. Lives here
  // because Layout is the signed-in staff shell; public and patient routes
  // render outside it and never report.
  useScreenTracking();

  // Refresh user profile (including permissions) from the API on every mount
  // so that permission changes take effect without requiring a logout/login.
  //
  // `ownerAgreementHold` must be lifted out explicitly rather than left to ride
  // along in `fresh`: updateUser merges into `user`, and the guard reads the
  // top-level store field, so anything passed through updateUser is written to
  // a field nothing reads. This is the only thing that applies the hold to a
  // session that was already signed in when it was published.
  useEffect(() => {
    if (!token) return;
    authApi.me().then(({ modules, ownerAgreementHold, ...fresh }) => {
      updateUser(fresh);
      setOwnerAgreementHold(ownerAgreementHold ?? false);
      // Keep the persisted per-account module map current so module-gated
      // nav filters correctly on the next first paint.
      if (modules) setModules(modules);
    }).catch(() => {
      // Token expired or API unreachable — silent fail, user keeps cached data
    });
  }, [token, accountId, updateUser, setModules, setOwnerAgreementHold]);

  useEffect(() => {
    if (!token) return;
    void usePatientHistoryStore.getState().hydrate();
    // Reset then re-hydrate so switching corporate accounts doesn't leak a
    // facility override from a prior accountID.
    useGlobalFacilityStore.getState().reset();
    void useGlobalFacilityStore.getState().hydrate();
  }, [token, accountId]);

  return (
    <NoteFormProvider>
      <VoiceCallProvider>
        <VideoCallProvider>
          {/* Below md the viewport lock is released (h-auto + overflow-visible)
              so pages scroll as a normal document; the shadcn wrapper's own
              min-h-svh keeps the shell filling the screen. md+ is unchanged. */}
          <SidebarProvider className="h-dvh overflow-hidden max-md:h-auto max-md:overflow-visible">
            <Sidebar />
            <PatientHistorySidebar />
            <SidebarInset className="z-10 overflow-hidden flex-1 min-w-0 max-md:overflow-visible">
              {/* The phone tier's only chrome. The AI trigger lives here rather than
                  floating over the page: as a fixed, vertically-centred button it sat
                  over whatever scrolled past the right edge and intercepted the tap.
                  This bar already existed and its right side was empty, so the move
                  costs no layout width. Gated on the same permission as <AISidebar />
                  below, which is where the tablet-band tab still lives. */}
              {/* `justify-between` rather than `ml-auto` on the sparkle (SCRUM-1480).
                  `.touch-target-inset` is `margin: -0.75rem` — the shorthand, so it sets
                  `margin-left`, and it is defined after `@tailwind utilities` outside
                  every `@layer`. Both it and `.ml-auto` are one class of specificity, so
                  source order decided and the negative margin won: on a coarse pointer
                  below 1024px the sparkle collapsed to the left, against the menu toggle,
                  with its hit box overhanging 12px further still. The gate is why nobody
                  saw it in a resized desktop window — that reports `pointer: fine`, the
                  rule never fires, and `ml-auto` worked.
                  Keep this row free of margin utilities. */}
              <header className="flex items-center justify-between h-12 px-3 border-b border-border md:hidden">
                <SidebarTrigger />
                {hasPermission(26) && <AIAssistantTrigger />}
              </header>
              {/* data-app-scroller marks the real scrollport for `useBodyScrollLock`.
                  Below md the shell releases the lock above and the document scrolls
                  instead; from md to lg this element is what a modal has to clamp, and
                  a hook cannot find it by structure without guessing. */}
              <main data-app-scroller className="relative z-10 flex-1 overflow-y-auto max-md:overflow-visible">
                <Outlet key={tzRulesKey} />
              </main>
            </SidebarInset>
            {hasPermission(26) && <AISidebar />}
            <ToastContainer />
            <FloatingVideoCall />
            <VideoCallConfirmDialog />
            <FloatingVoiceCall />
            <FloatingNoteForm />
            <NotificationModalHost />
          </SidebarProvider>
        </VideoCallProvider>
      </VoiceCallProvider>
    </NoteFormProvider>
  );
}
